import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useSearchParams } from 'react-router-dom'
import { Row } from 'src/shared/components/Semantic/all'
import { Card } from 'src/shared/components/ui/card'
import { LeftColumn } from './components/LeftColumn'
import { RightColumn } from './components/RightColumn'
import { createFormSchema } from './consts'
import { generateBrandfetchUrls, isValidCompanyUrl } from './utils'
import { useEmailDebounce, useLoginSignupSubmit } from './hooks'
import type { UseLoginSignupSubmitOptions } from './hooks'
import { AuthenticateFormData } from './types'
import { parseNextPath } from 'src/routes/paths'

type LoginSignupPanelBaseProps = {
  className?: string
  initialEmail?: string
  signupTitle?: string
  signupDescription?: string
  loginDescription?: string
  /** Replaces the illustration column and sits to the right of the form. */
  sideContent?: ReactNode
  /** Skip the Card chrome when the panel is already inside a dialog. */
  framed?: boolean
  /** Auth fields only — no logo, titles, or illustration column. */
  formOnly?: boolean
} & UseLoginSignupSubmitOptions

type LoginSignupPanelUrlMode = LoginSignupPanelBaseProps & {
  loginModeSource: 'url'
}

type LoginSignupPanelControlledMode = LoginSignupPanelBaseProps & {
  loginModeSource: 'controlled'
  isLoginMode: boolean
  onLoginModeChange: (login: boolean) => void
}

export type LoginSignupPanelProps =
  | LoginSignupPanelUrlMode
  | LoginSignupPanelControlledMode

export const LoginSignupPanel = (props: LoginSignupPanelProps) => {
  const [orgId, setOrgId] = useState<string | null>(null)
  const {
    onSignupOrgReady,
    className,
    nextPath: nextPathProp,
    signupTitle,
    signupDescription,
    loginDescription,
    sideContent,
    framed = true,
    formOnly = false,
    inviteOrganizationId,
  } = props

  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()

  const isLoginMode =
    props.loginModeSource === 'url'
      ? searchParams.get('login') !== null
      : props.isLoginMode

  const setLoginMode =
    props.loginModeSource === 'url'
      ? (login: boolean) => {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev)
              if (login) {
                next.set('login', '1')
              } else {
                next.delete('login')
              }
              return next
            },
            { replace: true }
          )
        }
      : props.onLoginModeChange

  const emailFromParams = searchParams.get('email') || ''
  const emailFromState = (location.state as { email?: string })?.email || ''
  const initialEmail =
    props.initialEmail || emailFromParams || emailFromState
  const nextPath =
    nextPathProp ?? parseNextPath(searchParams.get('next')) ?? undefined

  const formSchema = createFormSchema()
  const methods = useForm<AuthenticateFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: initialEmail,
    },
  })

  useEffect(() => {
    if (initialEmail) {
      methods.setValue('email', initialEmail)
    }
  }, [initialEmail, methods])

  const email = methods.watch('email')
  const debouncedCompanyUrl = useEmailDebounce(email)
  const imgUrls = generateBrandfetchUrls(debouncedCompanyUrl)
  const isCompanyUrlValid = isValidCompanyUrl(debouncedCompanyUrl)

  const {
    handleSubmit,
    loading,
    oauthLoading,
    startOAuth,
    isOAuthReturn,
    magicLinkSent,
    sentToEmail,
    resetMagicLinkState,
  } = useLoginSignupSubmit(orgId, setOrgId, isLoginMode, {
    onSignupOrgReady,
    nextPath,
    inviteOrganizationId,
  })

  useEffect(() => {
    if (!isLoginMode) {
      resetMagicLinkState()
    }
  }, [isLoginMode, resetMagicLinkState])

  const onSubmit = async (data: AuthenticateFormData) => {
    await handleSubmit(data, imgUrls)
  }

  const form = formOnly ? (
    <LeftColumn
      methods={methods}
      onSubmit={onSubmit}
      loading={loading}
      oauthLoading={oauthLoading}
      isOAuthReturn={isOAuthReturn}
      onOAuth={startOAuth}
      isCompanyUrlValid={!!inviteOrganizationId || isCompanyUrlValid}
      isLoginMode={isLoginMode}
      onLoginModeChange={setLoginMode}
      magicLinkSent={magicLinkSent}
      sentToEmail={sentToEmail}
      onResetMagicLink={resetMagicLinkState}
      formOnly
    />
  ) : (
    <Row
      className={
        sideContent
          ? 'min-h-[min(100%,640px)] min-w-0 overflow-hidden max-sm:!flex-col max-sm:min-h-0'
          : 'overflow-hidden'
      }
    >
      <LeftColumn
        methods={methods}
        onSubmit={onSubmit}
        loading={loading}
        oauthLoading={oauthLoading}
        isOAuthReturn={isOAuthReturn}
        onOAuth={startOAuth}
        isCompanyUrlValid={!!inviteOrganizationId || isCompanyUrlValid}
        isLoginMode={isLoginMode}
        onLoginModeChange={setLoginMode}
        magicLinkSent={magicLinkSent}
        sentToEmail={sentToEmail}
        onResetMagicLink={resetMagicLinkState}
        signupTitle={signupTitle}
        signupDescription={signupDescription}
        loginDescription={loginDescription}
        compact={!!sideContent}
      />
      {sideContent ?? <RightColumn />}
    </Row>
  )

  return (
    <div className={className ?? 'w-full'}>
      {framed ? (
        <Card
          variant={sideContent ? 'plain' : 'panel'}
          wrapperClassName="w-full"
          className={
            sideContent
              ? 'w-full min-h-[min(100%,640px)] min-w-0 gap-0 overflow-hidden rounded-[1.5rem] p-0 shadow-[0_24px_80px_hsl(220_20%_12%/0.12)] max-sm:min-h-0 max-sm:rounded-none max-sm:shadow-none'
              : 'overflow-hidden p-0'
          }
        >
          {form}
        </Card>
      ) : (
        form
      )}
    </div>
  )
}
